import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { Button, Modal, Input, Select, InputNumber, Spin, Empty, Switch, Typography } from "antd";
import { toast } from "../../components/toasts/toastStore";
import { apiServiciosInstance, authHeaderServicios } from "../../redux/actions/servicios/servicios";
import { SaveOutlined, CloseOutlined } from "@ant-design/icons";

import "./EventoDetallePage.css";

dayjs.locale("es");

const fmtFechaCorta = (v) => {
  if (!v) return "—";
  const d = dayjs(v);
  return d.isValid() ? d.format("D MMM YYYY, HH:mm") : "—";
};

const fmtDatetime = (v) => {
  if (!v) return "—";
  const d = dayjs(v);
  return d.isValid() ? d.format("DD/MM/YYYY, HH:mm") : "—";
};

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const money = (n) => (n == null ? "—" : `$${Number(n).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

const ACTION_CONFIG = {
  CREATE: { label: "Creacion", color: "#15803d", bg: "#dcfce7" },
  UPDATE: { label: "Actualizacion", color: "#f90336", bg: "#fee2e6" },
  DELETE: { label: "Eliminacion", color: "#b91c1c", bg: "#fee2e2" },
};
const getActionConfig = (a) => ACTION_CONFIG[a] || { label: a || "Cambio", color: "#595c5e", bg: "#f1f5f9" };

const { Text } = Typography;

const TIPO_LABEL = { blanco_negro: "Blanco y negro", color: "Color" };
const TAMANO_LABEL = { carta: "Carta", oficio: "Oficio" };

export default function ServicioDetallePage() {
  const { idServicio } = useParams();
  const navigate = useNavigate();

  const [servicio, setServicio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState("datos");

  const [actividad, setActividad] = useState([]);
  const [loadingActividad, setLoadingActividad] = useState(false);

  const [editingInfo, setEditingInfo] = useState(false);
  const [savingInfo, setSavingInfo] = useState(false);
  const [editForm, setEditForm] = useState({});

  const [tarifas, setTarifas] = useState([]);
  const [loadingTarifas, setLoadingTarifas] = useState(false);
  const [addTarifaOpen, setAddTarifaOpen] = useState(false);
  const [savingTarifa, setSavingTarifa] = useState(false);
  const [nuevaTarifa, setNuevaTarifa] = useState({});

  const fetchServicio = useCallback(() => {
    setLoading(true);
    apiServiciosInstance.get(`/servicios/${idServicio}`, { headers: authHeaderServicios() })
      .then((res) => setServicio(res.data))
      .catch(() => toast.error("No se pudo cargar el servicio"))
      .finally(() => setLoading(false));
  }, [idServicio]);

  useEffect(() => { fetchServicio(); }, [fetchServicio]);

  const fetchTarifas = useCallback(() => {
    setLoadingTarifas(true);
    apiServiciosInstance.get("/copias-tarifas", { headers: authHeaderServicios() })
      .then((res) => setTarifas(res.data?.items || []))
      .catch(() => {})
      .finally(() => setLoadingTarifas(false));
  }, []);

  useEffect(() => {
    if (activeTab === "tarifas") fetchTarifas();
  }, [activeTab, fetchTarifas]);

  useEffect(() => {
    if (activeTab !== "actividad" || !idServicio) return;
    setLoadingActividad(true);
    apiServiciosInstance.get(`/servicios/${idServicio}/actividad`, { headers: authHeaderServicios() })
      .then((res) => setActividad(res.data || []))
      .catch(() => {})
      .finally(() => setLoadingActividad(false));
  }, [activeTab, idServicio]);

  const startEditInfo = () => {
    setEditForm({
      nombre: servicio.nombre,
      precio_base: servicio.precio_base != null ? parseFloat(servicio.precio_base) : undefined,
      tasa_iva: servicio.tasa_iva != null ? parseFloat(servicio.tasa_iva) : undefined,
      usa_tarifa_copias: Boolean(servicio.usa_tarifa_copias),
    });
    setEditingInfo(true);
  };

  const handleSaveInfo = async () => {
    if (!editForm.nombre?.trim()) return toast.warning("El nombre es obligatorio");
    setSavingInfo(true);
    try {
      await apiServiciosInstance.patch(`/servicios/${idServicio}`, {
        nombre: editForm.nombre.trim(),
        precio_base: editForm.usa_tarifa_copias ? null : editForm.precio_base ?? null,
        tasa_iva: editForm.tasa_iva ?? null,
        usa_tarifa_copias: Boolean(editForm.usa_tarifa_copias),
      }, { headers: authHeaderServicios() });
      toast.success("Servicio actualizado correctamente");
      setEditingInfo(false);
      fetchServicio();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSavingInfo(false);
    }
  };

  const handleDelete = () => {
    Modal.confirm({
      title: "Eliminar este servicio?",
      content: "Esta accion desactiva el servicio. No se puede deshacer.",
      okText: "Eliminar",
      okButtonProps: { danger: true },
      cancelText: "Cancelar",
      onOk: async () => {
        setDeleting(true);
        try {
          await apiServiciosInstance.delete(`/servicios/${idServicio}`, { headers: authHeaderServicios() });
          toast.success("Servicio eliminado");
          navigate("/app/servicios");
        } catch (err) {
          toast.error(err?.response?.data?.detail || "No se pudo eliminar");
        } finally {
          setDeleting(false);
        }
      },
    });
  };

  const handleAddTarifa = async () => {
    if (!nuevaTarifa.tipo_copia || !nuevaTarifa.tamano || nuevaTarifa.hojas_desde == null || nuevaTarifa.precio_unitario == null) {
      toast.warning("Completa tipo, tamano, hojas desde y precio");
      return;
    }
    setSavingTarifa(true);
    try {
      await apiServiciosInstance.post("/copias-tarifas", nuevaTarifa, { headers: authHeaderServicios() });
      toast.success("Tarifa agregada");
      setAddTarifaOpen(false);
      setNuevaTarifa({});
      fetchTarifas();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo agregar la tarifa");
    } finally {
      setSavingTarifa(false);
    }
  };

  const handleDeleteTarifa = (id_tarifa_copia) => {
    Modal.confirm({
      title: "Eliminar esta tarifa?",
      okText: "Eliminar",
      okButtonProps: { danger: true },
      cancelText: "Cancelar",
      onOk: async () => {
        try {
          await apiServiciosInstance.delete(`/copias-tarifas/${id_tarifa_copia}`, { headers: authHeaderServicios() });
          toast.success("Tarifa eliminada");
          fetchTarifas();
        } catch (err) {
          toast.error(err?.response?.data?.detail || "No se pudo eliminar");
        }
      },
    });
  };

  if (loading) {
    return (
      <div className="cd-main" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!servicio) {
    return (
      <div className="cd-main" style={{ padding: 40 }}>
        <Empty description="Servicio no encontrado" />
      </div>
    );
  }

  return (
    <div className="cd-main">
      <div className="cd-content">
        <div className="cd-header-card">
          <div className="cd-header-top">
            <div>
              <div className="cd-header-name-row">
                <h1 className="cd-client-name">{(servicio.nombre || "—").toUpperCase()}</h1>
              </div>
              <div className="cd-header-meta">
                <span className="cd-meta-item">
                  <i className="fa-solid fa-calendar" /> Creado el {fmtFechaCorta(servicio.datetime)}
                </span>
              </div>
            </div>
            <div className="cd-header-actions">
              <Button icon={<i className="fa-solid fa-trash" />} className="cd-btn-delete" loading={deleting} onClick={handleDelete}>
                Eliminar
              </Button>
            </div>
          </div>

          <div className="cd-header-tabs">
            <button className={`cd-tab-btn ${activeTab === "datos" ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab("datos")}>
              <i className="fa-solid fa-file-lines" /> Informacion
            </button>
            {Boolean(servicio.usa_tarifa_copias) && (
              <button className={`cd-tab-btn ${activeTab === "tarifas" ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab("tarifas")}>
                <i className="fa-solid fa-copy" /> Tarifas por hojas
              </button>
            )}
            <button className={`cd-tab-btn ${activeTab === "actividad" ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab("actividad")}>
              <i className="fa-solid fa-clock" /> Actividad
            </button>
          </div>
        </div>

        {activeTab === "datos" && (
          <div className="cd-card cd-card-client">
            <div className="cd-card-header" style={{ justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div className="cd-card-icon-wrap"><i className="fa-solid fa-print"></i></div>
                <h2 className="cd-card-title">Informacion del servicio</h2>
              </div>
              {!editingInfo && (
                <Button icon={<i className="fa-solid fa-pen" />} className="cd-btn-edit" onClick={startEditInfo}>
                  Editar
                </Button>
              )}
            </div>

            {editingInfo ? (
              <div className="cd-edit-form">
                <div className="cd-edit-grid">
                  <div className="cd-edit-field">
                    <span className="cd-edit-label">Nombre</span>
                    <Input value={editForm.nombre} onChange={(e) => setEditForm((f) => ({ ...f, nombre: e.target.value }))} />
                  </div>
                  <div className="cd-edit-field">
                    <span className="cd-edit-label">Tasa de IVA %</span>
                    <InputNumber style={{ width: "100%" }} min={0} max={100} precision={2} value={editForm.tasa_iva} onChange={(v) => setEditForm((f) => ({ ...f, tasa_iva: v }))} />
                  </div>
                  <div className="cd-edit-field">
                    <span className="cd-edit-label">Usa tarifa por hojas</span>
                    <Switch checked={editForm.usa_tarifa_copias} onChange={(v) => setEditForm((f) => ({ ...f, usa_tarifa_copias: v }))} />
                  </div>
                  {!editForm.usa_tarifa_copias && (
                    <div className="cd-edit-field">
                      <span className="cd-edit-label">Precio</span>
                      <InputNumber style={{ width: "100%" }} min={0} precision={2} value={editForm.precio_base} onChange={(v) => setEditForm((f) => ({ ...f, precio_base: v }))} />
                    </div>
                  )}
                </div>
                <div className="cd-edit-actions">
                  <Button icon={<CloseOutlined />} onClick={() => setEditingInfo(false)} disabled={savingInfo}>Cancelar</Button>
                  <Button type="primary" icon={<SaveOutlined />} loading={savingInfo} onClick={handleSaveInfo} style={{ backgroundColor: "#f90336", borderColor: "#f90336" }}>
                    Guardar
                  </Button>
                </div>
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px 32px" }}>
                <div><span className="cd-field-label">Nombre</span><span className="cd-field-value">{capitalize(servicio.nombre)}</span></div>
                <div><span className="cd-field-label">Precio</span><span className="cd-field-value">{servicio.usa_tarifa_copias ? "Por tarifa de hojas" : money(servicio.precio_base)}</span></div>
                <div><span className="cd-field-label">Tasa de IVA</span><span className="cd-field-value">{servicio.tasa_iva != null ? `${servicio.tasa_iva}%` : "No aplica"}</span></div>
                <div><span className="cd-field-label">Fecha de creacion</span><span className="cd-field-value">{fmtFechaCorta(servicio.datetime)}</span></div>
              </div>
            )}
          </div>
        )}

        {activeTab === "tarifas" && (
          <div className="cd-card">
            <div className="cd-card-header" style={{ justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div className="cd-card-icon-wrap"><i className="fa-solid fa-copy"></i></div>
                <h2 className="cd-card-title">Tarifas por rango de hojas</h2>
              </div>
              <Button type="primary" icon={<i className="fa-solid fa-plus" />} onClick={() => setAddTarifaOpen(true)} style={{ backgroundColor: "#f90336", borderColor: "#f90336" }}>
                Agregar tarifa
              </Button>
            </div>
            <Text style={{ fontSize: 12, color: "#64748b", display: "block", marginBottom: 12 }}>
              Estas tarifas son compartidas por todos los servicios de copias, no solo por este.
            </Text>

            {loadingTarifas ? (
              <Spin />
            ) : tarifas.length === 0 ? (
              <Empty description="Todavia no hay tarifas configuradas" />
            ) : (
              <div className="cd-pagos-list">
                {tarifas.map((t) => (
                  <div key={t.id_tarifa_copia} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr 80px", gap: 12, padding: "12px 4px", borderBottom: "1px solid #f0edf2", alignItems: "center" }}>
                    <div><span className="cd-field-label">Tipo</span><div className="cd-field-value">{TIPO_LABEL[t.tipo_copia] || t.tipo_copia}</div></div>
                    <div><span className="cd-field-label">Tamano</span><div className="cd-field-value">{TAMANO_LABEL[t.tamano] || t.tamano}</div></div>
                    <div><span className="cd-field-label">Desde</span><div className="cd-field-value">{t.hojas_desde} hojas</div></div>
                    <div><span className="cd-field-label">Hasta</span><div className="cd-field-value">{t.hojas_hasta != null ? `${t.hojas_hasta} hojas` : "Sin limite"}</div></div>
                    <div><span className="cd-field-label">Precio por hoja</span><div className="cd-field-value">{money(t.precio_unitario)}</div></div>
                    <Button danger size="small" icon={<i className="fa-solid fa-trash" />} onClick={() => handleDeleteTarifa(t.id_tarifa_copia)} />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "actividad" && (
          <div className="cd-card">
            <div className="cd-card-header">
              <div className="cd-card-icon-wrap"><i className="fa-solid fa-clock"></i></div>
              <h2 className="cd-card-title">Actividad</h2>
              <span className="cd-actividad-count">{actividad.length}</span>
            </div>
            {loadingActividad ? (
              <Spin />
            ) : (
              <div className="cd-actividad-list">
                {actividad.map((item, idx) => {
                  const cfg = getActionConfig(item.action);
                  return (
                    <div key={item.id_audit_log || idx} className="cd-actividad-item">
                      <div className="cd-actividad-dot-col">
                        <div className="cd-actividad-dot" style={{ background: cfg.color }} />
                        {idx < actividad.length - 1 && <div className="cd-actividad-line" />}
                      </div>
                      <div className="cd-actividad-body">
                        <div className="cd-actividad-top-row">
                          <span className="cd-actividad-badge" style={{ color: cfg.color, background: cfg.bg }}>{cfg.label}</span>
                          <span className="cd-actividad-message">{item.message}</span>
                        </div>
                        <div className="cd-actividad-bottom-row">
                          <span className="cd-actividad-time">{fmtDatetime(item.datetime)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      <Modal
        open={addTarifaOpen}
        onCancel={() => setAddTarifaOpen(false)}
        onOk={handleAddTarifa}
        okText="Agregar"
        cancelText="Cancelar"
        title="Agregar tarifa por hojas"
        confirmLoading={savingTarifa}
        centered
        okButtonProps={{ style: { background: "#f90336", borderColor: "#f90336" } }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Select
            placeholder="Tipo de copia"
            options={[{ label: "Blanco y negro", value: "blanco_negro" }, { label: "Color", value: "color" }]}
            value={nuevaTarifa.tipo_copia}
            onChange={(v) => setNuevaTarifa((f) => ({ ...f, tipo_copia: v }))}
          />
          <Select
            placeholder="Tamano"
            options={[{ label: "Carta", value: "carta" }, { label: "Oficio", value: "oficio" }]}
            value={nuevaTarifa.tamano}
            onChange={(v) => setNuevaTarifa((f) => ({ ...f, tamano: v }))}
          />
          <InputNumber style={{ width: "100%" }} placeholder="Hojas desde" min={1} value={nuevaTarifa.hojas_desde} onChange={(v) => setNuevaTarifa((f) => ({ ...f, hojas_desde: v }))} />
          <InputNumber style={{ width: "100%" }} placeholder="Hojas hasta (vacio = sin limite)" min={1} value={nuevaTarifa.hojas_hasta} onChange={(v) => setNuevaTarifa((f) => ({ ...f, hojas_hasta: v }))} />
          <InputNumber style={{ width: "100%" }} placeholder="Precio por hoja" min={0} precision={2} value={nuevaTarifa.precio_unitario} onChange={(v) => setNuevaTarifa((f) => ({ ...f, precio_unitario: v }))} />
        </div>
      </Modal>
    </div>
  );
}
